import { useContext } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { TrashSimple } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { withErrorBoundary } from "@/hoc/with-error-boundary"
import { toast } from "@/hooks/useToast"
import { formatDate } from "@/lib/format-date.ts"
import { LastModificationInfo } from "@/modules/patients/detail/last-modification-info"
import { useMedications } from "@/modules/patients/detail/medication/hooks/useMedications.ts"
import { useLatestDiagnosisCode } from "@/modules/patients/detail/progress-note/hooks/useLatestDiagnosisCode"
import { ProgressNoteFormContext } from "@/modules/patients/detail/progress-note/hooks/useProgressNoteFormStore"
import { ReferralForm } from "@/modules/patients/detail/referral/de/de-referral-form"
import { useDeCreateReferralMutation$data } from "@/modules/patients/detail/referral/de/services/__generated__/useDeCreateReferralMutation.graphql.ts"
import { useDeUpdateReferralMutation$data } from "@/modules/patients/detail/referral/de/services/__generated__/useDeUpdateReferralMutation.graphql.ts"
import { deReferralLetterFragment } from "@/modules/patients/detail/referral/de/services/deReferralLetterFragment"
import { useDeCreateReferralMutation } from "@/modules/patients/detail/referral/de/services/useDeCreateReferralMutation"
import { useDeDeleteReferralMutation } from "@/modules/patients/detail/referral/de/services/useDeDeleteReferralMutation"
import { useDeGenerateReferralDocumentMutation } from "@/modules/patients/detail/referral/de/services/useDeGenerateReferralDocumentMutation.ts"
import { useDeUpdateReferralMutation } from "@/modules/patients/detail/referral/de/services/useDeUpdateReferralMutation"
import { type DeReferralSchema, deReferralSchema } from "@/modules/patients/detail/referral/schema/referral-schema"
import { useReferralStoreContext } from "@/modules/patients/detail/referral/store/useReferralStore"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam.ts"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { getDefaultDocumentLanguageValue } from "@/modules/patients/libs/document-languages.ts"
import { PatientAllergiesOutputFragment$key } from "@/modules/patients/services/__generated__/PatientAllergiesOutputFragment.graphql.ts"
import { PatientConditionInformationOutputFragment$key } from "@/modules/patients/services/__generated__/PatientConditionInformationOutputFragment.graphql.ts"
import { PatientAllergiesOutputFragment } from "@/modules/patients/services/PatientAllergiesOutputFragment.ts"
import { PatientConditionInformationOutputFragment } from "@/modules/patients/services/PatientConditionInformationOutputFragment.ts"
import { usePatientByIdQuery } from "@/modules/patients/services/usePatientByIdQuery.ts"
import { useConfirmStore } from "@/stores/useConfirmStore"
import { useDrawerStore } from "@/stores/useDrawerStore"

type ReferralLetter =
  | useDeCreateReferralMutation$data["de_createReferralLetter"]["de_ReferralLetter"]
  | useDeUpdateReferralMutation$data["de_updateReferralLetter"]["de_ReferralLetter"]

const DeReferralDrawer = withErrorBoundary((props: FetchKeyProps) => {
  const progressNoteFormStore = useContext(ProgressNoteFormContext)
  const { t } = useTranslation()
  const patientId = usePatientParam()
  const countryCode = useCountryParam()
  const appointmentId = useAppointmentParam()
  const [patientData] = usePatientByIdQuery(patientId, countryCode, appointmentId)
  const progressNoteId = useReferralStoreContext((state) => state.progressNoteId)
  const setProgressNoteId = useReferralStoreContext((state) => state.setProgressNoteId)
  const referralLetterRef = useReferralStoreContext((state) => state.referralLetterRef)
  const setReferralLetterRef = useReferralStoreContext((state) => state.setReferralLetterRef)
  const progressNoteCreatedDate = useReferralStoreContext((state) => state.progressNoteCreatedDate)

  const referralData = useFragment(deReferralLetterFragment, referralLetterRef)
  const isReferralFormEditMode = !!referralData?.id
  const updatedAt = referralData?.updatedAt || referralData?.createdAt
  const updatedBy = referralData?.updatedBy || referralData?.createdBy

  const { diagnosisCode: latestDiagnosisCode } = useLatestDiagnosisCode(progressNoteId || "")
  const { activeMedications, inactiveMedications } = useMedications()

  const medicationList = [...activeMedications, ...inactiveMedications]

  const { conditionInformation: conditionInformationRef, allergiesInformation: allergiesInformationRef } =
    patientData.patientById!

  const { conditions: conditionList } = useFragment<PatientConditionInformationOutputFragment$key>(
    PatientConditionInformationOutputFragment,
    conditionInformationRef,
  )

  const { allergies: allergyList } = useFragment<PatientAllergiesOutputFragment$key>(
    PatientAllergiesOutputFragment,
    allergiesInformationRef,
  )

  const documentData = referralData
    ? {
        diagnosisCode: referralData?.icdDiagnosis.diagnosisCode,
        reasoning: referralData.reasoning || "",
        allergies: referralData?.allergies || [],
        conditions: referralData?.conditions || [],
        medicationIds: referralData?.medicationIds || [],
        documentLanguage:
          "documentLanguage" in referralData
            ? getDefaultDocumentLanguageValue(referralData.documentLanguage)
            : undefined,
      }
    : null

  const form = useForm<DeReferralSchema>({
    resolver: zodResolver(deReferralSchema),
    defaultValues: documentData ?? {
      diagnosisCode: latestDiagnosisCode,
      reasoning: "",
    },
  })

  const [createReferral, isCreating] = useDeCreateReferralMutation()
  const [updateReferral, isUpdating] = useDeUpdateReferralMutation()
  const [deleteReferral, isDeleting] = useDeDeleteReferralMutation()
  const [generateDeReferralDocument, isGenerating] = useDeGenerateReferralDocumentMutation()
  const closeDrawer = useDrawerStore((state) => state.closeDrawer)
  const confirm = useConfirmStore((state) => state.confirm)
  const cancel = useConfirmStore((state) => state.cancel)

  const handleCloseDrawer = () => {
    setProgressNoteId("")
    closeDrawer()
    setReferralLetterRef(null)
  }

  const clearReferralLetterRequiredError = (progressNoteIdToClear: string) => {
    if (!progressNoteFormStore) return

    const progressNoteFormState = progressNoteFormStore.getState()
    const progressNoteForm = progressNoteFormState.progressNoteForms.get(progressNoteIdToClear)
    if (!progressNoteForm) return

    progressNoteFormState.setProgressNoteForm(progressNoteIdToClear, progressNoteForm.episodeOfCareProgressNoteData, {
      kind: "DeBetterDocReferralLetter",
      data: { hasErrors: false },
    })
  }

  const onSubmit = (data: DeReferralSchema) => {
    if (!progressNoteId) return

    if (isReferralFormEditMode) {
      updateReferral({
        variables: {
          input: {
            reasoning: data.reasoning,
            id: referralData.id,
            allergies: data?.allergies || [],
            conditions: data?.conditions || [],
            medicationIds: data?.medicationIds || [],
            diagnosisCode: data.diagnosisCode,
          },
        },
        onCompleted: (response) => {
          const referral = response.de_updateReferralLetter.de_ReferralLetter
          const id = referral?.id

          if (id) {
            generateDocument(id, data, referral)
          }
        },
      })
    } else {
      createReferral({
        variables: {
          input: {
            reasoning: data.reasoning,
            patientId: patientId,
            progressNoteId: progressNoteId,
            allergies: data?.allergies || [],
            conditions: data?.conditions || [],
            medicationIds: data?.medicationIds || [],
            diagnosisCode: data.diagnosisCode,
          },
        },
        onCompleted: (response) => {
          const referral = response.de_createReferralLetter.de_ReferralLetter
          const id = referral?.id

          clearReferralLetterRequiredError(progressNoteId)

          if (id) {
            generateDocument(id, data, referral)
          }
        },
        updater(store, data) {
          try {
            const progressNoteRecordProxy = store.get(progressNoteId)
            const referralLetter = data?.de_createReferralLetter?.de_ReferralLetter

            if (referralLetter?.id) {
              const newRecordProxy = store.get(referralLetter.id)
              if (newRecordProxy) {
                progressNoteRecordProxy?.setLinkedRecord(newRecordProxy, "de_referralLetter")
              }
            }
          } catch (error) {
            console.error("Error while processing data:", error)
          }
        },
      })
    }
  }

  const handleDelete = () => {
    if (!progressNoteId || !referralData) return

    confirm({
      confirmText: "common.button.delete",
      cancelText: "common.button.keep",
      title: `${t("common.button.delete")} ${t("episodeOfCare.progressNote.referralLetter.default")}`,
      content: "episodeOfCare.deleteProgressNote.content",
      onConfirm: () =>
        deleteReferral({
          variables: {
            input: {
              id: referralData.id,
            },
          },
          onCompleted: () => {
            cancel()
            handleCloseDrawer()
            toast({
              description: t("patient.referralLetter.deleteMessage.success"),
            })
          },
        }),
    })
  }

  const generateDocument = (id: string, formData: DeReferralSchema, response: ReferralLetter) => {
    if (!progressNoteId) return

    const { allergies = [], medicationIds = [], conditions = [], reasoning = "" } = formData

    const episodeOfCare = response?.icdDiagnosis.text ?? ""

    const formattedAllergies = allergies.map((id) => {
      return allergyList.find((item) => item.icdDiagnosis.id === id)?.icdDiagnosis.text ?? ""
    })
    const formattedConditions = conditions.map((id) => {
      return conditionList.find((item) => item.icdDiagnosis.id === id)?.icdDiagnosis.text ?? ""
    })
    const formattedMedications = medicationIds.map((id) => {
      return medicationList.find((item) => item.id === id)?.name ?? ""
    })

    generateDeReferralDocument({
      params: {
        id,
        allergies: formattedAllergies?.join(", ") || "-",
        conditions: formattedConditions?.join(", ") || "-",
        medications: formattedMedications?.join(", ") || "-",
        dateOfConsultation: formatDate(progressNoteCreatedDate),
        specialtyName: response?.progressNote?.pointOfCare?.pointOfCareSpecialty?.word || "",
        reasoning,
        episodeOfCare,
      },
      onCompleted() {
        handleCloseDrawer()
      },
      onError() {
        handleCloseDrawer()
      },
    })
  }

  return (
    <Drawer open onOpenChange={handleCloseDrawer}>
      <FormProvider {...form}>
        <DrawerContent>
          <DrawerClose className="top-2 right-2" />
          <DrawerHeader className="mb-4 pr-4" data-testid="referral-header">
            <DrawerTitle className="animate-fade-in-bottom text-on-surface-subdued text-sm font-medium">
              {t("episodeOfCare.progressNote.referral")}
            </DrawerTitle>
          </DrawerHeader>
          <DrawerBody className="flex gap-4 pt-0.5" data-testid="referral-body">
            <div className="grid gap-8">
              {!!progressNoteId && <ReferralForm fetchKey={props.fetchKey} progressNoteId={progressNoteId} />}
            </div>
          </DrawerBody>
          <DrawerFooter className="flex-wrap gap-2" data-testid="referral-footer">
            {referralData && (
              <div className="flex gap-2">
                {updatedAt && updatedBy && <LastModificationInfo updatedAt={updatedAt} updatedBy={updatedBy} />}
                <button onClick={handleDelete} data-testid="de-sick-leave-delete-button">
                  <TrashSimple className="text-on-surface-subdued" />
                </button>
              </div>
            )}
            <div className="ml-auto flex gap-2">
              <Button variant="secondary" data-testid="referral-cancel-button" onClick={handleCloseDrawer}>
                {t("common.button.cancel")}
              </Button>
              <Button
                isLoading={isCreating || isUpdating || isDeleting || isGenerating}
                onClick={() => {
                  FaroInsightsWrapper.pushEvent("documents_referral_save")
                  form.handleSubmit(onSubmit)()
                }}
                data-testid="referral-save-button">
                {t("common.button.save")}
              </Button>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </FormProvider>
    </Drawer>
  )
}, "DeReferralDrawer")

export { DeReferralDrawer }
